import { isEqual } from "lodash-es";

import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import LegacyFilterModel, {
  CombinedCondition,
  FilterCondition,
  FilterModel,
  LegacyCombinedCondition,
  LegacyFilterCondition,
  LegacySimpleCondition,
  SimpleCondition,
} from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";

import { formatDate, formatRawDate } from "@app/util/formatters";
import { dateOperators } from "@app/util/viewModel/const";

import { isCombinedCondition, isLegacyCombinedCondition } from "./assert";

export const areLegacySimpleConditionsEqual = (
  a: LegacySimpleCondition,
  b: LegacySimpleCondition
): boolean => {
  if (a.operator !== b.operator || Array.isArray(a.value) !== Array.isArray(b.value)) {
    return false;
  }
  return Array.isArray(a.value) && Array.isArray(b.value)
    ? isEqual([...a.value].sort(), [...b.value].sort()) // compare values in any order
    : isEqual(a.value, b.value);
};

export const areLegacyCombinedConditionsEqual = (
  a: LegacyCombinedCondition,
  b: LegacyCombinedCondition
): boolean => {
  return (
    a.operator === b.operator &&
    a.conditions.length === b.conditions.length &&
    a.conditions.every((aCondition, index) =>
      areLegacyConditionsEqual(aCondition, b.conditions[index])
    )
  );
};

export const areLegacyConditionsEqual = (
  a: LegacyFilterCondition,
  b: LegacyFilterCondition
): boolean => {
  const isACombined = isLegacyCombinedCondition(a);
  const isBCombined = isLegacyCombinedCondition(b);

  if (isACombined !== isBCombined) {
    return false;
  }

  // enough to check only one
  if (isACombined) {
    return areLegacyCombinedConditionsEqual(
      a as LegacyCombinedCondition,
      b as LegacyCombinedCondition
    );
  } else {
    return areLegacySimpleConditionsEqual(a as LegacySimpleCondition, b as LegacySimpleCondition);
  }
};

export const areLegacyFiltersEqual = (a: LegacyFilterModel, b: LegacyFilterModel): boolean => {
  const aFieldFilterNames = Object.keys(a);
  const bFieldFilterNames = Object.keys(b);

  if (aFieldFilterNames.length !== bFieldFilterNames.length) {
    return false;
  }

  return aFieldFilterNames.every(
    (fieldFilterName) =>
      // we need to check if both A and B have this field because previous length check only
      // ensured that the number of fields is equal, although the set of fields can still be different
      a[fieldFilterName] &&
      b[fieldFilterName] &&
      areLegacyConditionsEqual(a[fieldFilterName], b[fieldFilterName])
  );
};

const DATE_REGEX = /^\d{4}-\d{2}-\d{2}/;

const isDateValue = (value: any): boolean =>
  value instanceof Date || (typeof value === "string" && DATE_REGEX.test(value));

const formatDateValue = (value: any): string =>
  value instanceof Date ? formatDate(value, "yyyy-MM-dd") : formatRawDate(value, "yyyy-MM-dd");

export const areSimpleConditionsEqual = (a: SimpleCondition, b: SimpleCondition): boolean => {
  if (
    a.fieldFilterName !== b.fieldFilterName ||
    a.operator !== b.operator ||
    Array.isArray(a.value) !== Array.isArray(b.value)
  ) {
    return false;
  }

  if (Array.isArray(a.value) && Array.isArray(b.value)) {
    // for dates with date operators we should only compare the date part, but not the time
    // let's check if the condition looks like the in_range condition
    if (
      a.operator === FilterOperator.IN_RANGE &&
      a.value.length === 2 &&
      b.value.length === 2 &&
      isDateValue(a.value[0]) &&
      isDateValue(b.value[0])
    ) {
      return (
        isEqual(formatDateValue(a.value[0]), formatDateValue(b.value[0])) &&
        isEqual(formatDateValue(a.value[1]), formatDateValue(b.value[1]))
      );
    }

    return isEqual([...a.value].sort(), [...b.value].sort()); // compare values in any order
  }

  // for dates with date operators we should only compare the date part, but not the time
  if (isDateValue(a.value) && isDateValue(b.value) && dateOperators.has(a.operator)) {
    return isEqual(formatDateValue(a.value), formatDateValue(b.value));
  }

  return isEqual(a.value, b.value);
};

export const areCombinedConditionsEqual = (a: CombinedCondition, b: CombinedCondition): boolean => {
  if (a.operator !== b.operator || a.tag !== b.tag || a.conditions.length !== b.conditions.length) {
    return false;
  }

  const matchedIndices = new Set<number>();

  // yes, this loop has O(n^2) complexity, but it's acceptable for small arrays
  // and our conditions are usually small
  return a.conditions.every((aCondition) => {
    const matchIndex = b.conditions.findIndex(
      (bCondition, index) =>
        !matchedIndices.has(index) && areConditionsEqual(aCondition, bCondition)
    );
    if (matchIndex === -1) {
      return false;
    }
    matchedIndices.add(matchIndex);
    return true;
  });
};

export const areConditionsEqual = (a: FilterCondition, b: FilterCondition): boolean => {
  const isACombined = isCombinedCondition(a);
  const isBCombined = isCombinedCondition(b);

  if (isACombined !== isBCombined || a.operator !== b.operator) {
    return false;
  }

  // enough to check only one
  if (isACombined) {
    return areCombinedConditionsEqual(a as CombinedCondition, b as CombinedCondition);
  } else {
    return areSimpleConditionsEqual(a as SimpleCondition, b as SimpleCondition);
  }
};

export const areFiltersEqual = (a: FilterModel, b: FilterModel): boolean => {
  return areConditionsEqual(a, b);
};
